import React, { useEffect, useState } from 'react';
import PageTitle from '../../components/PageTitle';
import { useNavigate, useParams } from 'react-router-dom';
import PatientInformationHeader from '../../components/Patient/PatientInformationHeader';
import { useGetPatientDetailsQuery, useGetPatientOrdersQuery } from '../../features/patient/patientService';
import RenderSideItem from '../../components/RenderSideItem';
import { Card } from 'antd';
import '../../styles/_patientCard.scss';
import RoundedButton from '../../components/RoundedButton';
import PatientApproveMedicationsCard from '../../components/Patient/PatientApproveMedicationsCard';
import { OrderResponse, OrderStatusEnum } from '../../models/PatientModels';
import { LoadingSpinner } from '../../components/Spinner/LoadingSpinner';
import OrderBodyMessage from '../../components/Orders/OrderBodyMessage';

const PatientApproveMedications = () => {
    const navigate = useNavigate();
    const { id: patientId = '', from = '' } = useParams();

    const { data: patientDetails } = useGetPatientDetailsQuery({
        id: patientId,
    });

    const [isLoadingRefetch, setIsLoadingRefetch] = useState(false);

    const {
        data: patientOrders,
        isLoading,
        refetch,
    } = useGetPatientOrdersQuery({
            patientId: patientId,
            orderStatus: OrderStatusEnum.Pending,
        }
    );


    useEffect(() => {
        // Needed due to order status changing through webhook, which takes couple of seconds to go through the cycle
        // Have to use isLoadingRefetch because redux's is not modifyable and will trigger on refetch only, 
        // which creates a delay where there is no loading and no data, empty screen
        // the system needs to wait 2 seconds before the request so that the data comes through webhook
        setIsLoadingRefetch(true) 
        const timeout = setTimeout(async () => {
            await refetch();
            setIsLoadingRefetch(false);
        }, 2000);

        return () => clearTimeout(timeout);

    }, []);

    useEffect(() => {
        if (localStorage.hasOwnProperty('allMedications')) {
            const data = JSON.parse(
                localStorage.getItem('allMedications') as string,
            );
            const newData = data.find(
                (d: any) => !d.hasOwnProperty('approveMedication'),
            );
            if (newData && from === 'submitAll') {
                navigate(
                    `/patient/${patientId}/add-approve-medication/${newData.scriptSureOrderId}/${newData.id}`,
                );
            } else {
                localStorage.removeItem('allMedications');
            }
        }
    }, [from, navigate, patientId]);

    const submitAllMedications = () => {
        localStorage.setItem('allMedications', JSON.stringify(patientOrders));
        if (patientOrders && patientOrders.length > 0) {
            navigate(
                `/patient/${patientId}/add-approve-medication/${patientOrders[0].scriptSureOrderId}/${patientOrders[0].id}`,
            );
        }
    };

    return (
        <div>
            <PageTitle
                title="Review Medications"
                onClick={() => navigate(`/patient/${patientId}`)}
            />

            <div className="mx-5">
                {patientDetails && (
                    <PatientInformationHeader
                        patient={patientDetails}
                        navigate={() => navigate(`/patient/${patientId}`)}
                    >
                        <RenderSideItem
                            link={`/patient/${patientId}/medications-history`}
                            iconSource="/assets/rx-images/patient-dashboard/med-history.png"
                            title="Medication History"
                        />
                        <RenderSideItem
                            link={`/patient/${patientId}/allergies`}
                            title="Drug Allergies"
                            iconSource="/assets/rx-images/patient-dashboard/patient-allergies.png"
                        />
                    </PatientInformationHeader>
                )}
            </div>
            {(isLoading || isLoadingRefetch) && <LoadingSpinner fontSize={32} />}
            {!isLoading && !isLoadingRefetch &&
                patientOrders &&
                (patientOrders.length === 0 ? (
                    <div className="mx-5 mt-4">
                        <OrderBodyMessage>
                            There are no pending approval to be displayed.
                        </OrderBodyMessage>
                    </div>
                ) : (
                    <Card className="mx-5 shadow-sm bg-body mt-4">
                        <div className="display-flex align-items-center justify-content-between">
                            <div className="approve-count-title">
                                <span className="pending-number">
                                    {patientOrders?.length}
                                </span>
                                <span className="approve-count-lable">
                                    Medications Pending Submission
                                </span>
                            </div>
                            <RoundedButton
                                onClick={submitAllMedications}
                                className="approve-submit-all-button"
                                style={{
                                    backgroundColor: 'var(--hcrx-medium-blue)',
                                    color: 'white',
                                }}
                            >
                                Submit All Medications
                            </RoundedButton>
                        </div>
                        <PatientApproveMedicationsCard orders={patientOrders} />
                    </Card>
                ))}
        </div>
    );
};

export default PatientApproveMedications;
